/**
 * API Reference Section Components
 * Stripe + Apple Developer + Vercel documentation style
 */

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// Code block component for API examples
const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language = 'json' }) => (
  <div className="relative group">
    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
      <button
        onClick={() => navigator.clipboard.writeText(code)}
        className="px-3 py-1.5 rounded-md text-xs"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: '#FFFFFF',
          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
          fontWeight: 500,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          cursor: 'pointer'
        }}
      >
        Copy
      </button>
    </div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs" style={{ color: '#666666', fontFamily: "'Inter', sans-serif" }}>
        {language.toUpperCase()}
      </span>
    </div>
    <pre className="p-4 rounded-lg overflow-x-auto" style={{
      backgroundColor: '#1A1A1A',
      color: '#2ECC71',
      fontFamily: "'Monaco', 'Courier New', monospace",
      fontSize: '13px',
      lineHeight: '1.6'
    }}>
      <code>{code}</code>
    </pre>
  </div>
);

// Parameter table component
const ParameterTable: React.FC<{ parameters: Array<{ name: string, type: string, description: string, required?: boolean }> }> = ({ parameters }) => (
  <div className="overflow-x-auto">
    <table className="w-full" style={{ borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Parameter</th>
          <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Type</th>
          <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Description</th>
        </tr>
      </thead>
      <tbody>
        {parameters.map((param, i) => (
          <tr key={i} style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
            <td className="py-3 px-4">
              <code style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', padding: '2px 6px', borderRadius: '4px', fontFamily: "'Monaco', monospace", fontSize: '12px' }}>
                {param.name}
              </code>
              {param.required && <span style={{ color: '#E74C3C', fontSize: '12px', marginLeft: '4px' }}>*</span>}
            </td>
            <td className="py-3 px-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '13px' }}>{param.type}</td>
            <td className="py-3 px-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '13px' }}>{param.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Introduction Section
export const IntroductionSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Introduction
      </h2>

      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        The Mernaki API is a RESTful API that allows you to integrate Mernaki with your internal systems, automate job postings, manage applications, and use Mernaki's tools programmatically.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {[
          { title: 'RESTful Design', desc: 'Standard HTTP methods and predictable URLs' },
          { title: 'JSON Responses', desc: 'All responses are in JSON format with consistent structure' },
          { title: 'Rate Limited', desc: 'Fair usage policies to ensure API reliability' },
          { title: 'Secure Authentication', desc: 'API key authentication with HTTPS encryption' }
        ].map((feature, i) => (
          <div key={i} className="p-5 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <h4 className="text-lg mb-2" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>{feature.title}</h4>
            <p style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', lineHeight: '1.6' }}>{feature.desc}</p>
          </div>
        ))}
      </div>

      <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(74, 144, 226, 0.08)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
        <h3 className="text-lg mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Base URL</h3>
        <CodeBlock code="https://api-gateway.internal.mernaki.de/v1" language="url" />
      </div>
    </motion.div>
  );
};

// Authentication Section
export const AuthenticationSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Authentication
      </h2>

      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        All API requests require an API key passed in the Authorization header.
      </p>

      <div className="mb-8">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Authorization Header</h3>
        <CodeBlock code="Authorization: Bearer <YOUR_API_KEY>" language="http" />
      </div>

      <div className="mb-8">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Example Request</h3>
        <CodeBlock code={`curl -X GET "https://api-gateway.internal.mernaki.de/v1/jobs" \\
  -H "Authorization: Bearer jb_live_abc123..." \\
  -H "Content-Type: application/json"`} language="bash" />
      </div>

      <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(231, 76, 60, 0.08)', border: '1px solid rgba(231, 76, 60, 0.2)' }}>
        <h4 className="text-lg mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Key Rules</h4>
        <ul className="space-y-2">
          {[
            'Never share your API key publicly',
            'Replace your key if you suspect misuse',
            'Keys are project-specific',
            'Store keys securely in environment variables'
          ].map((rule, i) => (
            <li key={i} className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: '#E74C3C' }} />
              <span style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px' }}>{rule}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

// Base URLs Section
export const BaseUrlsSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Base URLs
      </h2>

      <div className="space-y-6">
        <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <h3 className="text-xl mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Production</h3>
          <CodeBlock code="https://api-gateway.internal.mernaki.de/v1" language="url" />
          <p className="mt-2" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px' }}>
            Use this URL for all production API calls
          </p>
        </div>

        <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <h3 className="text-xl mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Sandbox</h3>
          <CodeBlock code="https://api-sandbox.internal.mernaki.de/v1" language="url" />
          <p className="mt-2" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px' }}>
            Use this URL for testing and development
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Rate Limits Section
export const RateLimitsSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Rate Limits
      </h2>

      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        The Mernaki API implements rate limiting to ensure fair usage and maintain service quality.
      </p>

      <div className="overflow-x-auto mb-6">
        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
              <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Plan</th>
              <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Requests per minute</th>
              <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Burst limit</th>
            </tr>
          </thead>
          <tbody>
            {[
              { plan: 'Free', rpm: '100', burst: '200' },
              { plan: 'Pro', rpm: '1,000', burst: '2,000' },
              { plan: 'Enterprise', rpm: '10,000', burst: '20,000' }
            ].map((limit, i) => (
              <tr key={i} style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                <td className="py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>{limit.plan}</td>
                <td className="py-3 px-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '13px' }}>{limit.rpm}</td>
                <td className="py-3 px-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '13px' }}>{limit.burst}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(74, 144, 226, 0.08)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
        <h4 className="text-lg mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Rate Limit Headers</h4>
        <p className="mb-3" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px' }}>
          Every API response includes rate limit information in the headers:
        </p>
        <CodeBlock code={`X-RateLimit-Limit: 1000
X-RateLimit-Remaining: 999
X-RateLimit-Reset: 1640995200`} language="http" />
      </div>
    </motion.div>
  );
};

// Jobs API Section
export const JobsApiSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Jobs API
      </h2>

      {/* List Jobs */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>List Jobs</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(46, 204, 113, 0.1)', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
          <code style={{ color: '#2ECC71', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            GET /api/v1/jobs
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Returns all active job listings associated with your Mernaki project.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Query Parameters</h4>
        <ParameterTable parameters={[
          { name: 'page', type: 'number', description: 'Page index (default: 1)' },
          { name: 'limit', type: 'number', description: 'Number of jobs per page (default: 10, max: 100)' },
          { name: 'status', type: 'string', description: 'Filter by status: "active", "draft", "archived"' },
          { name: 'location', type: 'string', description: 'Filter by location' }
        ]} />

        <h4 className="text-lg mb-4 mt-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Response Example</h4>
        <CodeBlock code={`{
  "data": [
    {
      "id": "jb_12345",
      "title": "Software Engineer",
      "location": "Berlin, Germany",
      "type": "Full-time",
      "department": "Engineering",
      "status": "active",
      "createdAt": "2026-06-10T14:30:00Z",
      "updatedAt": "2026-06-10T14:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 10,
    "total": 25,
    "totalPages": 3
  }
}`} />
      </div>

      {/* Create Job */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Create Job</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(74, 144, 226, 0.1)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
          <code style={{ color: '#4A90E2', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            POST /api/v1/jobs
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Creates a new job listing in your Mernaki project.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Request Body</h4>
        <ParameterTable parameters={[
          { name: 'title', type: 'string', description: 'Job title', required: true },
          { name: 'description', type: 'string', description: 'Job description in HTML or markdown', required: true },
          { name: 'location', type: 'string', description: 'Job location', required: true },
          { name: 'type', type: 'string', description: 'Employment type: "Full-time", "Part-time", "Contract"', required: true },
          { name: 'department', type: 'string', description: 'Department name' },
          { name: 'salary', type: 'object', description: 'Salary information with min, max, currency' }
        ]} />

        <h4 className="text-lg mb-4 mt-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Request Example</h4>
        <CodeBlock code={`{
  "title": "Senior Frontend Developer",
  "description": "We are looking for an experienced frontend developer...",
  "location": "Berlin, Germany",
  "type": "Full-time",
  "department": "Engineering",
  "salary": {
    "min": 70000,
    "max": 90000,
    "currency": "EUR"
  }
}`} />
      </div>

      {/* Update Job */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Update Job</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
          <code style={{ color: '#FFC107', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            PUT /api/v1/jobs/:id
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Updates an existing job listing. Only provided fields will be updated.
        </p>
      </div>

      {/* Delete Job */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Delete Job</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(231, 76, 60, 0.1)', border: '1px solid rgba(231, 76, 60, 0.2)' }}>
          <code style={{ color: '#E74C3C', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            DELETE /api/v1/jobs/:id
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Permanently deletes a job listing. This action cannot be undone.
        </p>
      </div>
    </motion.div>
  );
}

// Applications API Section
export const ApplicationsApiSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Applications API
      </h2>

      {/* Submit Application */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Submit Application</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(74, 144, 226, 0.1)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
          <code style={{ color: '#4A90E2', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            POST /api/v1/jobs/:jobId/applications
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Submits a new application for a specific job listing.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Request Body</h4>
        <ParameterTable parameters={[
          { name: 'firstName', type: 'string', description: 'Applicant first name', required: true },
          { name: 'lastName', type: 'string', description: 'Applicant last name', required: true },
          { name: 'email', type: 'string', description: 'Applicant email address', required: true },
          { name: 'phone', type: 'string', description: 'Phone number' },
          { name: 'coverLetter', type: 'string', description: 'Cover letter text' },
          { name: 'resumeUrl', type: 'string', description: 'URL to uploaded resume file' }
        ]} />

        <h4 className="text-lg mb-4 mt-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Response Example</h4>
        <CodeBlock code={`{
  "id": "app_899933",
  "jobId": "jb_77821",
  "firstName": "John",
  "lastName": "Doe",
  "email": "john.doe@example.com",
  "status": "submitted",
  "submittedAt": "2026-11-12T14:55:10Z"
}`} />
      </div>

      {/* List Applications */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>List Applications</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(46, 204, 113, 0.1)', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
          <code style={{ color: '#2ECC71', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            GET /api/v1/applications
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Returns all applications for your Mernaki project.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Query Parameters</h4>
        <ParameterTable parameters={[
          { name: 'jobId', type: 'string', description: 'Filter applications by job ID' },
          { name: 'status', type: 'string', description: 'Filter by status: "submitted", "reviewed", "accepted", "rejected"' },
          { name: 'page', type: 'number', description: 'Page index (default: 1)' },
          { name: 'limit', type: 'number', description: 'Number of applications per page (default: 20)' }
        ]} />
      </div>

      {/* Application Status Update */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Application Status Update</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
          <code style={{ color: '#FFC107', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            PATCH /api/v1/applications/:id/status
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Updates the status of an application.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Request Body</h4>
        <CodeBlock code={`{
  "status": "reviewed",
  "notes": "Candidate looks promising, scheduling interview"
}`} />
      </div>
    </motion.div>
  );
};

// Brand Settings API Section
export const BrandSettingsApiSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Brand Settings API
      </h2>

      {/* Get Branding Config */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Get Branding Config</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(46, 204, 113, 0.1)', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
          <code style={{ color: '#2ECC71', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            GET /api/v1/branding
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Returns the current branding configuration for your Mernaki project.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Response Example</h4>
        <CodeBlock code={`{
  "companyName": "Acme Corp",
  "logo": "https://cdn.mernaki.de/logos/acme-logo.png",
  "colors": {
    "primary": "#4A90E2",
    "secondary": "#2ECC71",
    "background": "#F7F5F5"
  },
  "fonts": {
    "heading": "Inter",
    "body": "Inter"
  },
  "customCss": "/* Custom styles */"
}`} />
      </div>

      {/* Update Branding Config */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Update Branding Config</h3>
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
          <code style={{ color: '#FFC107', fontFamily: "'Monaco', monospace", fontSize: '14px', fontWeight: 600 }}>
            PUT /api/v1/branding
          </code>
        </div>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Updates the branding configuration. Only provided fields will be updated.
        </p>
      </div>
    </motion.div>
  );
};

// Webhooks Section
export const WebhooksSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Webhook Events
      </h2>

      <p className="mb-8" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Mernaki can send webhook notifications to your application when certain events occur. This allows you to automate workflows and keep your systems in sync.
      </p>

      {/* Job Created */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>job.created</h3>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Triggered whenever a new job is created in your Mernaki project.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Payload Example</h4>
        <CodeBlock code={`{
  "event": "job.created",
  "data": {
    "id": "jb_12345",
    "title": "Software Engineer",
    "location": "Berlin, Germany",
    "type": "Full-time",
    "status": "active",
    "createdAt": "2026-11-12T14:30:00Z"
  },
  "timestamp": "2026-11-12T14:30:05Z"
}`} />
      </div>

      {/* Job Updated */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>job.updated</h3>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Triggered whenever a job listing is updated.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Payload Example</h4>
        <CodeBlock code={`{
  "event": "job.updated",
  "data": {
    "id": "jb_12345",
    "title": "Senior Software Engineer",
    "changes": ["title", "salary"],
    "updatedAt": "2026-11-12T15:45:00Z"
  },
  "timestamp": "2026-11-12T15:45:05Z"
}`} />
      </div>

      {/* Applicant Submitted */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>applicant.submitted</h3>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Triggered whenever a candidate submits a job application.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Payload Example</h4>
        <CodeBlock code={`{
  "event": "applicant.submitted",
  "data": {
    "applicationId": "app_899933",
    "jobId": "jb_77821",
    "applicant": {
      "firstName": "John",
      "lastName": "Doe",
      "email": "john.doe@example.com"
    },
    "submittedAt": "2025-11-12T14:55:10Z"
  },
  "timestamp": "2025-11-12T14:55:15Z"
}`} />
      </div>

      {/* Applicant Reviewed */}
      <div className="mb-12">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>applicant.reviewed</h3>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Triggered whenever an application status is updated.
        </p>

        <h4 className="text-lg mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Payload Example</h4>
        <CodeBlock code={`{
  "event": "applicant.reviewed",
  "data": {
    "applicationId": "app_899933",
    "jobId": "jb_77821",
    "status": "accepted",
    "previousStatus": "reviewed",
    "reviewedAt": "2025-11-12T16:20:00Z"
  },
  "timestamp": "2025-11-12T16:20:05Z"
}`} />
      </div>

      <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(74, 144, 226, 0.08)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
        <h4 className="text-lg mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Webhook Configuration</h4>
        <p className="mb-3" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px' }}>
          Configure webhook endpoints in your Mernaki dashboard under Settings → Webhooks. All webhook requests include a signature header for verification.
        </p>
        <CodeBlock code="X-Mernaki-Signature: sha256=..." language="http" />
      </div>
    </motion.div>
  );
};

// Errors Section
export const ErrorsSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="mb-20"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Error Handling
      </h2>

      <p className="mb-8" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        The Mernaki API uses conventional HTTP response codes to indicate the success or failure of an API request.
      </p>

      <h3 className="text-2xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Error Codes</h3>

      <div className="overflow-x-auto mb-8">
        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
              <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Code</th>
              <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Message</th>
              <th className="text-left py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              { code: '200', message: 'OK', description: 'Request successful' },
              { code: '201', message: 'Created', description: 'Resource created successfully' },
              { code: '400', message: 'Bad Request', description: 'Missing or invalid parameters' },
              { code: '401', message: 'Unauthorized', description: 'Invalid API key' },
              { code: '403', message: 'Forbidden', description: 'Insufficient permissions' },
              { code: '404', message: 'Not Found', description: 'Resource does not exist' },
              { code: '429', message: 'Rate limit exceeded', description: 'Too many requests' },
              { code: '500', message: 'Internal Server Error', description: 'Something went wrong on our end' }
            ].map((error, i) => (
              <tr key={i} style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                <td className="py-3 px-4">
                  <code style={{
                    backgroundColor: error.code.startsWith('2') ? 'rgba(46, 204, 113, 0.1)' :
                      error.code.startsWith('4') ? 'rgba(231, 76, 60, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                    color: error.code.startsWith('2') ? '#2ECC71' :
                      error.code.startsWith('4') ? '#E74C3C' : '#FFC107',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontFamily: "'Monaco', monospace",
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    {error.code}
                  </code>
                </td>
                <td className="py-3 px-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>{error.message}</td>
                <td className="py-3 px-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '13px' }}>{error.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="text-2xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Error Response Format</h3>
      <CodeBlock code={`{
  "error": {
    "code": "validation_error",
    "message": "The request parameters are invalid",
    "details": [
      {
        "field": "email",
        "message": "Email address is required"
      }
    ]
  }
}`} />

      <div className="mt-8 p-6 rounded-xl" style={{ backgroundColor: 'rgba(46, 204, 113, 0.08)', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
        <h4 className="text-lg mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>Need Help?</h4>
        <p style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', lineHeight: '1.6' }}>
          If you need support integrating the API, contact: <strong>developer@mernaki.de</strong>
        </p>
      </div>
    </motion.div>
  );
};